import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

function Code({ children }) {
  return (
    <pre className="mt-3 overflow-x-auto rounded-xl border border-border bg-muted/60 p-4 text-xs leading-relaxed text-foreground">
      <code>{children}</code>
    </pre>
  );
}

function Step({ n, title, children }) {
  return (
    <section className="relative border-l-2 border-border pl-8 pb-10 last:pb-0">
      <span className="absolute -left-[15px] top-0 inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary font-display text-xs font-bold text-primary-foreground">
        {n}
      </span>
      <h2 className="font-display text-xl font-semibold text-foreground">{title}</h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

export default function Setup() {
  return (
    <div className="container-page py-16">
      <div className="max-w-2xl">
        <h1 className="font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
          Setup guide
        </h1>
        <p className="mt-3 text-lg text-muted-foreground">
          About ten minutes end to end. Install both apps, add your keys, point the processor at a
          Google Sheet, and you are recording.
        </p>
        <div className="mt-6">
          <Link to="/download">
            <Button variant="outline">
              Get the installers <ArrowRight size={16} />
            </Button>
          </Link>
        </div>
      </div>

      <div className="mt-12 max-w-2xl">
        <Step n={1} title="Install both apps">
          <p>
            Download <strong>Call Recorder</strong> and <strong>Call Processor</strong> from the
            download page, unzip each, and run the installer inside. Install the Recorder first, then
            the Processor.
          </p>
          <p>
            Both apps auto-update on launch, so you only download them here once.
          </p>
        </Step>

        <Step n={2} title="Clear the Windows App Control prompt">
          <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-amber-700 dark:text-amber-300">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <p className="text-sm">
              Windows may show a blue “Windows protected your PC” screen because the installers are
              signed with our own certificate. Click <strong>More info</strong>, then{' '}
              <strong>Run anyway</strong>. This is expected during launch and does not mean the file
              is unsafe.
            </p>
          </div>
        </Step>

        <Step n={3} title="Add your API keys (.env)">
          <p>
            In the Call Processor install folder, copy <code>.env.example</code> to <code>.env</code>{' '}
            and fill in your keys. These stay on your machine.
          </p>
          <Code>{`# .env — Call Processor
OPENAI_API_KEY=sk-...          # or your chosen LLM provider key
GROK_API_KEY=xai-...           # optional, if using Grok for analysis
GOOGLE_SHEET_ID=1AbC...        # the Sheet that collects your calls
GOOGLE_APPS_SCRIPT_URL=https://script.google.com/macros/s/AKfy.../exec`}</Code>
          <p>Save the file. The processor reads it on launch.</p>
        </Step>

        <Step n={4} title="Set your identity (config.toml)">
          <p>
            Open <code>config.toml</code> next to the Processor and set the fields the follow-up
            packet signs off with. This is who the follow-up “comes from.”
          </p>
          <Code>{`# config.toml — your identity
[identity]
name = "Jordan Rep"
company = "Acme Sales Co."
email = "jordan@acme.co"
phone = "+1 555 010 0142"
calendar_link = "https://cal.com/jordan"

[followup]
tone = "warm-professional"
timezone = "America/New_York"`}</Code>
        </Step>

        <Step n={5} title="Connect your Google Sheet (Apps Script)">
          <p>
            The processor writes one row per call to a Google Sheet through a small Apps Script web
            app. Set it up once:
          </p>
          <ol className="ml-4 list-decimal space-y-1.5">
            <li>Create a Google Sheet and copy its ID from the URL into <code>GOOGLE_SHEET_ID</code>.</li>
            <li>In the Sheet, open <strong>Extensions → Apps Script</strong>.</li>
            <li>Paste the script from the Processor’s <code>apps-script/</code> folder.</li>
            <li>Deploy it as a <strong>Web app</strong> (execute as you, access: anyone with the link).</li>
            <li>Copy the deployment URL into <code>GOOGLE_APPS_SCRIPT_URL</code> in your <code>.env</code>.</li>
          </ol>
        </Step>

        <Step n={6} title="First run and model download">
          <p>
            Launch the Call Processor. On first run it downloads the transcription model
            (<code>faster-whisper small.en</code>, about 500&nbsp;MB) and stores it next to the app.
            After that it works offline for transcription.
          </p>
          <p>
            Start the Call Recorder, make a call, and check your Google Sheet — a new row with the
            summary and next step should appear once the Processor finishes.
          </p>
        </Step>
      </div>

      <div className="mt-4 max-w-2xl rounded-2xl border border-border bg-card/50 p-6">
        <p className="text-sm text-muted-foreground">
          Stuck on a step? Your account token on the{' '}
          <Link to="/dashboard" className="font-medium text-primary hover:underline dark:text-accent">
            dashboard
          </Link>{' '}
          identifies your install when you reach out for help.
        </p>
      </div>
    </div>
  );
}
